/**
 * Panel Dock Strip — renders every dock entry on one screen edge, in strip
 * order: the fixed 'toolbar' rail slot and the docked panel instances.
 * Order comes from useDockLayoutStore, so panels and the toolbar interleave
 * freely on a shared edge.
 */

import React from 'react';
import { useDockLayoutStore, DockSide } from '../store/useDockLayoutStore';
import {
    usePanelShell,
    TOOLBAR_DOCK_ENTRY_ID,
    DEFAULT_PANEL_INSTANCE_ID,
} from './PanelShell';
import { PanelInstance } from './PanelInstance';

export interface PanelDockStripProps {
    side: DockSide;
}

export const PanelDockStrip: React.FC<PanelDockStripProps> = ({ side }) => {
    const strip = useDockLayoutStore((s) => s.strips[side]);
    const shell = usePanelShell();

    const entries = strip.filter(
        (entryId) =>
            (entryId === TOOLBAR_DOCK_ENTRY_ID && shell.toolbarSlot !== null) ||
            shell.panels.some((p) => p === entryId),
    );
    if (entries.length === 0) return null;

    return (
        <>
            {entries.map((entryId, index) =>
                entryId === TOOLBAR_DOCK_ENTRY_ID ? (
                    <React.Fragment key="toolbar">
                        {shell.toolbarSlot}
                    </React.Fragment>
                ) : (
                    <PanelInstance
                        key={entryId}
                        registry={shell.registry}
                        panelId={entryId}
                        panelPosition={side}
                        dockIndex={index}
                        setPanelPosition={(position) =>
                            // Edge-only setter used by FloatingPanel
                            // conversion: move to the end of that strip
                            useDockLayoutStore
                                .getState()
                                .dockEntry(entryId, {
                                    side: position,
                                    index: useDockLayoutStore.getState()
                                        .strips[position].length,
                                })
                        }
                        onAddPanel={shell.addPanel}
                        onRemovePanel={shell.removePanel}
                        floatingWindows={shell.floatingWindows}
                        setFloatingWindows={shell.setFloatingWindows}
                    />
                ),
            )}
        </>
    );
};

export { DEFAULT_PANEL_INSTANCE_ID };
